import {createRecipePlan} from './recipe-process.js';
import {recipeTranslation} from './recipe.js';
import {normaliseControlledSafetyRules,recipeTrustPresentation} from './trust.js';

function text(value){return String(value??'').trim();}

function fold(value){
  return text(value).normalize('NFD').replace(/[\u0300-\u036f]/g,'').toLocaleLowerCase('pl').replace(/ł/g,'l');
}

function minutes(plan){
  let total=0,count=0;
  for(const prep of plan.preparations){
    for(const value of [prep.curingDurationMinutes,prep.dryingDurationMinutes,prep.preparationDurationMinutes]){
      if(value!=null){total+=Number(value);count++;}
    }
  }
  for(const target of plan.stageTargets){
    if(target.plannedDurationMinutes!=null){total+=Number(target.plannedDurationMinutes);count++;}
  }
  return count?total:null;
}

function compatibility(plan,capabilities){
  if(!capabilities)return 'unknown';
  const min=Number(capabilities.minTemperatureCelsius);
  const max=Number(capabilities.maxTemperatureCelsius);
  if(!Number.isFinite(min)||!Number.isFinite(max))return 'unknown';

  let hasTarget=false;
  for(const target of plan.stageTargets){
    const values=[target.targetChamberCelsius,target.minChamberCelsius,target.maxChamberCelsius]
      .filter(value=>value!=null)
      .map(Number);
    if(values.length)hasTarget=true;
    if(values.some(value=>value<min||value>max))return 'incompatible';
  }
  return hasTarget?'compatible':'review';
}

export function createRecipeLibraryEntry(plan,{
  locale='pl',
  productCategories=[],
  difficulty='unknown',
  experienceGuidance=null,
  smokerCapabilities=null,
  safetyRules=[],
  safetyNotes=[],
  tips=[]
}={}){
  const value=createRecipePlan(plan);
  const translation=recipeTranslation(value.recipe,{locale});
  const products=value.items.map(item=>item.name);
  const stageTypes=[...new Set(value.stages.map(stage=>stage.typeKey))];
  const primaryWood=value.woods[0]?.name||null;

  return Object.freeze({
    id:value.recipe.id,
    plan:value,
    title:translation.title,
    description:translation.description,
    type:value.recipe.type,
    visibility:value.recipe.visibility,
    authorUserId:value.recipe.authorUserId,
    authorName:value.recipe.authorName,
    sourceName:value.recipe.sourceName,
    sourceUrl:value.recipe.sourceUrl,
    version:value.recipe.version,
    products:Object.freeze(products),
    productCategories:Object.freeze([...(productCategories||[])].map(x=>text(x).toLowerCase()).filter(Boolean)),
    stageTypes:Object.freeze(stageTypes),
    primaryWood,
    approximateMinutes:minutes(value),
    difficulty:text(difficulty)||'unknown',
    experienceGuidance:text(experienceGuidance)||null,
    compatibility:compatibility(value,smokerCapabilities),
    trust:recipeTrustPresentation(value.recipe),
    safetyRules:normaliseControlledSafetyRules(safetyRules||[]),
    userSafetyNotes:Object.freeze([...(safetyNotes||[])].map(text).filter(Boolean)),
    tips:Object.freeze([...(tips||[])].map(text).filter(Boolean))
  });
}

export function discoverRecipes(entries=[],{
  scope='public',
  userId=null,
  query='',
  productCategory=null,
  stageType=null,
  compatibility:compatibilityFilter=null,
  savedRecipeIds=[]
}={}){
  const owner=text(userId);
  const savedIds=new Set([...(savedRecipeIds||[])].map(String));
  const term=fold(query);
  const category=text(productCategory).toLowerCase();
  const stage=text(stageType).toLowerCase();
  const compatible=text(compatibilityFilter).toLowerCase();

  return Object.freeze((entries||[])
    .filter(Boolean)
    .filter(entry=>{
      if(scope==='mine')return owner&&entry.authorUserId===owner;
      if(scope==='saved'){
        if(!savedIds.has(String(entry.id)))return false;
        if(entry.visibility==='private')return owner&&entry.authorUserId===owner;
        return ['official','community'].includes(entry.type);
      }
      return entry.visibility==='public'
        &&(entry.type==='official'||entry.type==='community');
    })
    .filter(entry=>!term||fold([
      entry.title,
      entry.description,
      ...(entry.products||[])
    ].join(' ')).includes(term))
    .filter(entry=>!category||(entry.productCategories||[]).includes(category))
    .filter(entry=>!stage||(entry.stageTypes||[]).includes(stage))
    .filter(entry=>!compatible||entry.compatibility===compatible)
    .sort((a,b)=>
      (a.type==='official'?0:1)-(b.type==='official'?0:1)
      ||String(a.title).localeCompare(String(b.title),'pl')
      ||String(a.id).localeCompare(String(b.id))
    ));
}

export function recipeLibraryCard(entry){
  if(!entry?.id)throw new TypeError('Recipe library entry is required');
  return Object.freeze({
    id:entry.id,
    title:entry.title,
    type:entry.type,
    source:entry.sourceName||entry.authorName||null,
    products:entry.products,
    approximateMinutes:entry.approximateMinutes,
    primaryWood:entry.primaryWood,
    difficulty:entry.difficulty,
    experienceGuidance:entry.experienceGuidance,
    compatibility:entry.compatibility
  });
}
